/**
 * analyseFitPolling.js — RC 1.48.1 frontend polling helper.
 *
 * Wraps the W.2 generic run framework (`POST /api/runs` +
 * `GET /api/runs/{id}`) for the `analyse_fit` action. Callers
 * receive the same canonical `{ score, analysis, billing }` shape
 * regardless of whether the backend served the response
 * synchronously (sync_200) or asynchronously (async_202).
 *
 * RC 1.49.7 W.2 (2026-05-09): submit + poll URLs migrated from
 * the per-action endpoints (`/api/jobs/{id}/analyze-fit` +
 * `/api/analyse-runs/{id}`) to the generic wrapper. The
 * legacy endpoints remain mounted on the backend; this helper
 * is the only frontend caller affected.
 *
 * Responsibilities:
 *   - Submit via `POST /api/runs` with `action_code='analyse_fit'`.
 *   - Detect a 202 envelope (`served_via === 'async_202'` and a
 *     non-null `run_id`).
 *   - Poll `GET /api/runs/{id}` with capped exponential back-off
 *     until status ∈ { complete, failed, cancelled }.
 *   - Translate the polled `result` block into the canonical
 *     200-shape so existing UI consumers (JobDetailPage,
 *     AnalyseFitSection, JobApplicationWizard) do not branch.
 *   - Write / clear a generic `runMarker` so a same-tab refresh
 *     or tab-return rehydrates the in-flight UI without
 *     re-submission.
 *   - Visibility hook — on tab return, force an immediate poll
 *     if the next scheduled poll is more than 2 s away.
 *   - Abort cleanly on AbortSignal (component unmount /
 *     navigation), throwing a DOMException-shaped AbortError.
 *   - Honour the frontend kill-switch
 *     `REACT_APP_RC_1_48_1_ASYNC` (default `true`). When `false`,
 *     a 202 envelope is treated as a hard error and the helper
 *     throws — no polling is attempted.
 *
 * Strict rails: pricing, credits, charge gate, tolerance band,
 * prompts, model routing — none referenced from this module.
 */

import { apiFetch } from './api';
import { BUNDLE_VERSION } from './buildMeta';
import { playCompletionChime } from './completionChime';
import { findRecentFitForSource, isTimeoutError } from './timeoutRecovery';
import {
  writeRunMarker,
  bumpRunMarker,
  clearRunMarker,
} from './runMarker';

/** Polling cadence (ms). Caps at 8 s; total ceiling ~120 s. */
const POLL_DELAYS_MS = [2000, 3000, 4000, 6000, 8000];
const MAX_POLLS = 30; // ~120 s upper bound
const ASYNC_FLAG_DEFAULT_ON = true;

/**
 * Read the frontend kill-switch.
 * Default ON unless explicitly set to a false alias.
 */
export function isAsyncPollingEnabled() {
  const raw = (process.env.REACT_APP_RC_1_48_1_ASYNC ?? '').trim().toLowerCase();
  if (raw === '') return ASYNC_FLAG_DEFAULT_ON;
  return ['1', 'true', 'yes', 'on'].includes(raw);
}

/** A 202 envelope from `POST /api/runs` looks like:
 *     { action_code: 'analyse_fit',
 *       run_id: '<uuid>',
 *       status: 'queued' | 'running',
 *       served_via: 'async_202',
 *       result: null,
 *       billing: null,
 *       poll_url: '/api/analyse-runs/<id>',
 *       estimated_completion_seconds: int|null }
 */
function isAsyncEnvelope(env) {
  return (
    env &&
    typeof env === 'object' &&
    env.served_via === 'async_202' &&
    typeof env.run_id === 'string' &&
    env.run_id.length > 0
  );
}

/** A sync 200 envelope from `POST /api/runs` looks like:
 *     { action_code: 'analyse_fit',
 *       run_id: <uuid>|null,
 *       status: 'complete',
 *       served_via: 'sync_200',
 *       result: <canonical analyse-fit body>,
 *       billing: <billing block>,
 *       ... }
 */
function isSyncCompleteEnvelope(env) {
  return (
    env &&
    typeof env === 'object' &&
    env.served_via === 'sync_200' &&
    env.result &&
    typeof env.result === 'object'
  );
}

/** Sleep helper that respects an AbortSignal. */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(makeAbortError());
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(makeAbortError());
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

function makeAbortError() {
  // DOMException is available in browsers + jsdom.
  if (typeof DOMException === 'function') {
    return new DOMException('Polling aborted', 'AbortError');
  }
  const e = new Error('Polling aborted');
  e.name = 'AbortError';
  return e;
}

/**
 * Poll `GET /api/runs/{id}` until the run reaches a terminal
 * status. Returns a "polled" object normalised to the legacy
 * analyse_runs row shape so `synthesiseCanonical` works unchanged.
 *
 * RC 1.49.7 W.2 — wraps the visibility-change event so a tab
 * returning from background fires an immediate poll if the next
 * scheduled tick is more than 2 s away. This catches the case
 * where browser throttling stretched the polling timer to a
 * minute-plus while the tab was hidden.
 */
async function pollAnalyseRun(analyseRunId, { signal, markerScope } = {}) {
  // Visibility-fired immediate-poll flag. The next iteration
  // checks-and-clears this on entry.
  let visibilityWake = false;
  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      visibilityWake = true;
    }
  };
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  try {
    for (let i = 0; i < MAX_POLLS; i += 1) {
      if (signal?.aborted) throw makeAbortError();
      const baseDelay = POLL_DELAYS_MS[Math.min(i, POLL_DELAYS_MS.length - 1)];
      // If a visibility wake fired since the last tick, skip the
      // remaining sleep and poll right away.
      const delay = visibilityWake ? 0 : baseDelay;
      visibilityWake = false;
      if (delay > 0) {
        await sleep(delay, signal);
      }
      if (signal?.aborted) throw makeAbortError();

      const env = await apiFetch(`/api/runs/${analyseRunId}`);
      const status = env?.status;

      // Bump the run marker so a refresh during the poll loop
      // surfaces the most recent observed status without an extra
      // network roundtrip.
      if (markerScope && status) {
        bumpRunMarker({ ...markerScope, status });
      }

      if (status === 'complete' || status === 'failed' || status === 'cancelled') {
        // Long-running completion notification only.
        // Fire the success chime ONLY on terminal success. Skipped for
        // failed / cancelled. Internally dedupe-guarded so the polling
        // loop's last tick + the resolver cannot double-fire.
        if (status === 'complete') {
          try { playCompletionChime(); } catch (_) { /* silent */ }
        }
        // Normalise the wrapper envelope to the legacy
        // analyse_runs row shape `synthesiseCanonical` expects:
        // it reads `polled.result`, `polled.id`, `polled.served_via`.
        return {
          id:           env.run_id,
          status:       env.status,
          result:       env.result,
          served_via:   env.served_via,
          error_code:   env.error_code,
          error_detail: env.error_detail,
        };
      }
      // status === 'queued' | 'running' | 'aggregating' → keep polling.
    }
    const err = new Error(
      'Fit analysis is still running after the maximum wait. ' +
      'Please try again in a moment.',
    );
    err.code = 'POLL_TIMEOUT';
    throw err;
  } finally {
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  }
}

/**
 * Translate a completed polled response into the canonical 200-shape
 * the rest of the frontend already consumes.
 *
 * Canonical 200 shape (truncated to consumed fields):
 *   { score: { id, fit_score, cv_sub_score, analyse_run_id, ... },
 *     analysis: { fit_score, cv_sub_score, ... },
 *     billing: { cached, was_free_fit, usage_consumed, ... } }
 *
 * Polled 'complete' shape:
 *   { id (run id), job_score_id, status: 'complete',
 *     result: { fit_score_median, cv_sub_score_canonical, ... }, ... }
 */
function synthesiseCanonical(polled) {
  const r = polled.result || {};
  const fitMedian = r.fit_score_median;
  const cvCanonical = r.cv_sub_score_canonical;
  // Backend stores fit_score in unit form (0..1) on job_scores rows;
  // the polled median is 0..100. Mirror the unit form to keep the
  // synthesised shape isomorphic with the sync 200 path's `score`
  // object so downstream `(fit <= 1 ? fit*100 : fit)` normalisation
  // works identically.
  const fitUnit = (typeof fitMedian === 'number')
    ? fitMedian / 100
    : null;

  // ── H2 — Polling envelope completeness (operator-locked, 2026-02)
  // The polled `result` now mirrors sync-200's `analysis` block
  // when the backend's H1 enrichment fires (i.e. on a terminal
  // run that points at a job_scores row). Forward those richer
  // fields verbatim so the UI consumes a result indistinguishable
  // from a hard-reload `/best-fit` hydrate.
  //
  // Fields with a recovered/sparse fallback are preserved
  // (cv_sub_score → r.cv_sub_score_canonical mirror); fields that
  // simply did not exist on the legacy poll envelope (key
  // strengths, repositioning advice, interview prep, ...) now
  // light up correctly. The dead `r.interview_prep ?? null` line
  // pre-RC 1.51 is removed in favour of a single explicit assignment
  // — the backend NOW populates it.
  const cvSub = (r.cv_sub_score !== undefined && r.cv_sub_score !== null)
    ? r.cv_sub_score
    : (cvCanonical ?? null);

  return {
    score: {
      id: r.job_score_id || null,
      fit_score: fitUnit,
      cv_sub_score: cvSub,
      analyse_run_id: polled.id || null,
      // Provenance hints surface in the receipt panel (RC 1.47.P1.5)
      // and the future B.3 decision-badge surface, but are not
      // required by today's consumers.
      governance_decision: r.governance_decision || null,
      variance_band: r.variance_band || null,
      // H2 — surface the final_decision so PackSection's
      // canonical-fit banner does not need a second `/best-fit`
      // probe to render verdict copy.
      final_decision: r.final_decision || null,
    },
    analysis: {
      fit_score: fitUnit,
      cv_sub_score: cvSub,
      // Sub-axis scores — pre-H2 the recovered path simply did
      // not have these. Now they round-trip from the H1 enrichment.
      final_decision:    r.final_decision ?? null,
      salary_score:      r.salary_score ?? null,
      seniority_score:   r.seniority_score ?? null,
      location_score:    r.location_score ?? null,
      strategic_score:   r.strategic_score ?? null,
      risk_score:        r.risk_score ?? null,
      // Cv sub-score variants (RC 1.46.DUAL-GRADER) — preserved
      // across recovery paths so the JobDetail / Workspace panels
      // render verdict + provisional flag identically.
      cv_sub_score_holistic:    r.cv_sub_score_holistic ?? null,
      cv_sub_score_strict:      r.cv_sub_score_strict ?? null,
      cv_sub_score_provisional: !!r.cv_sub_score_provisional,
      // Reasoning text (key gap H1 closes).
      reasoning_summary:    r.reasoning_summary ?? '',
      key_strengths:        r.key_strengths ?? [],
      key_risks:            r.key_risks ?? [],
      score_explanation:    r.score_explanation ?? '',
      repositioning_advice: r.repositioning_advice ?? '',
      // RC 1.47 Phase 2 — interview_prep payload. Pre-H1 this was
      // ALWAYS undefined on the polled envelope (the dead-code
      // forward at RC 1.50 R.3 surfaced null silently). H1 now
      // populates it from job_scores.reasoning_json.
      interview_prep:       r.interview_prep ?? null,
    },
    // RC 1.50 R.3 — top-level forward retained for the Workspace
    // Interview Prep section's mount-time hydration. Same source
    // as `analysis.interview_prep` above.
    interview_prep: r.interview_prep ?? null,
    billing: {
      // The user's credit was decremented at request time on the
      // backend; no new charge happens during polling. Surface
      // `cached: false` + `usage_consumed: null` so the existing
      // toast cascade falls through to the neutral
      // "Fit analysis complete." branch.
      cached: false,
      was_free_fit: null,
      usage_consumed: null,
    },
    _served_via: polled.served_via || 'poll_200',
  };
}

/**
 * Synthesise the canonical 200-shape from a `BestFitCachedRow`
 * returned by `GET /api/jobs/{id}/best-fit`. Used by the
 * RC 1.49.1 P0-2 recovery path: when the initial POST 504s but
 * the backend persisted a fresh job_scores row in the background,
 * the wizard / job-detail caller should consume the same shape
 * it would have on a 200 sync response.
 *
 * `BestFitCachedRow` carries `fit_score` as 0..100 (already scaled
 * by `_scale_fit_score`); the canonical shape mirrors the unit
 * form (0..1) to remain isomorphic with the sync 200 path.
 */
function synthesiseCanonicalFromCachedRow(row) {
  const fit100 = typeof row?.fit_score === 'number' ? row.fit_score : null;
  const fitUnit = fit100 != null ? fit100 / 100 : null;
  const cv = row?.reasoning_json?.cv_sub_score ?? null;
  return {
    score: {
      id: row?.score_id || null,
      fit_score: fitUnit,
      cv_sub_score: cv,
      analyse_run_id: null,
      governance_decision: row?.final_decision || null,
      variance_band: null,
    },
    analysis: {
      fit_score: fitUnit,
      cv_sub_score: cv,
    },
    // RC 1.50 R.3 — forward persisted interview_prep so the
    // recovered/cached path renders the same 5-bucket view as a
    // fresh run. Read-only.
    interview_prep: row?.reasoning_json?.interview_prep ?? null,
    billing: {
      // Recovery never bills — backend already handled charge in the
      // original run that 504'd. `cached: true` so the toast cascade
      // falls through to the neutral "Existing analysis loaded"
      // branch instead of charging a second Credit message.
      cached: true,
      was_free_fit: null,
      usage_consumed: null,
    },
    _served_via: 'recovery_504',
  };
}

/**
 * Submit an analyse-fit request. Returns the canonical 200-shape.
 *
 * @param {string} jobId            UUID of the target job.
 * @param {object} requestBody      The body for POST /analyze-fit.
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] Aborts polling on unmount/navigation.
 * @returns {Promise<{score, analysis, billing}>}
 * @throws  {Error} on abort, failed run, or polling timeout.
 */
/**
 * Submit an analyse-fit request. Returns the canonical 200-shape.
 *
 * @param {string} jobId            UUID of the target job.
 * @param {object} requestBody      The body for POST /analyze-fit.
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] Aborts polling on unmount/navigation.
 * @param {(state:'checking'|'recovered'|'failed')=>void} [opts.onRecoveryState]
 *   RC 1.49.1 P0-2 — UI banner hook for the 30s ingress 504 recovery
 *   loop. Fires when the initial POST itself throws a recoverable
 *   timeout error and the helper falls back to polling
 *   `/api/jobs/{id}/best-fit` for a freshly-completed job_scores row.
 * @returns {Promise<{score, analysis, billing}>}
 * @throws  {Error} on abort, failed run, or polling timeout.
 */
export async function runAnalyzeFitWithPolling(jobId, requestBody, opts = {}) {
  const {
    signal, onRecoveryState, userId,
    // RC 1.51 (operator-locked, 2026-02) — Workspace Interview Prep
    // is a separately-billed product action with its own ledger
    // line. The helper accepts an `actionCode` so the same polling
    // primitives serve both `analyse_fit` (baseline diagnostic) and
    // `interview_prep` (workspace deliverable). Default preserves
    // backward compatibility for the 7 existing call sites.
    actionCode = 'analyse_fit',
  } = opts;
  if (signal?.aborted) throw makeAbortError();

  // RC 1.49.1 P0-2 — capture submit time BEFORE the POST so the
  // recovery poll can disambiguate a row that was persisted by
  // *this* run from any older cached row for the same source.
  const submittedAtMs = Date.now();

  // RC 1.49.7 W.2 — the run-marker scope. We only have enough
  // information to write a marker when the caller passed `userId`
  // and the request body identifies a source. Sections that don't
  // pass `userId` (e.g. legacy callers) skip the marker logic
  // entirely; the helper continues to work without it.
  const sourceId = requestBody?.source_id || null;
  const markerScope = (
    userId && jobId && sourceId
      ? {
        actionCode,
        userId,
        jobId,
        sourceId,
      }
      : null
  );

  let env;
  try {
    // RC 1.49.7 W.2 / RC 1.51 — submit via the generic run framework.
    // Action code is parameterised so the same helper serves
    // analyse_fit (baseline) and interview_prep (workspace
    // deliverable). The wrapper proxies to the registered adapter
    // and reshapes the response into a uniform envelope.
    env = await apiFetch('/api/runs', {
      method: 'POST',
      // RC 1.55 Phase Z.4 — read-only forensic telemetry. See
      // cvVariantPolling.js for the strict-hold rationale. The
      // dispatch surface here is the analyse_fit polling helper;
      // both Workspace AnalyseFitSection and JobDetailPage's
      // baseline-fit path share this helper, so the label is
      // helper-scoped not section-scoped.
      headers: {
        'X-Ndor-Dispatch-Surface': 'workspace_analyse_fit_section_v1',
        'X-Ndor-Bundle-Version':   BUNDLE_VERSION,
      },
      body: {
        action_code: actionCode,
        payload: { job_id: jobId, ...(requestBody || {}) },
        idempotency_key: requestBody?.idempotency_key,
      },
    });
  } catch (err) {
    // Abort signals always bubble up unchanged.
    if (err?.name === 'AbortError') throw err;
    if (!isTimeoutError(err)) throw err;
    // Recovery path. Poll best-fit for a fresh row matching the
    // requested source. Two attempts × 8s mirrors the
    // CV / cover-letter recovery wrapper. We give the backend an
    // initial 8s head-start because the typical 504 fires AT 30s
    // and the run usually completes within a few seconds afterwards.
    onRecoveryState?.('checking');
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (signal?.aborted) throw makeAbortError();
      await sleep(8000, signal);
      if (signal?.aborted) throw makeAbortError();
      const row = await findRecentFitForSource(jobId, requestBody || {}, submittedAtMs);
      if (row) {
        onRecoveryState?.('recovered');
        return synthesiseCanonicalFromCachedRow(row);
      }
    }
    onRecoveryState?.('failed');
    throw err;
  }

  // Sync 200 fast-path — the wrapper envelope's `result` field
  // already carries the canonical { score, analysis, billing }
  // shape from the existing handler.
  if (isSyncCompleteEnvelope(env)) {
    return env.result;
  }

  // 202 path. Honour the kill-switch.
  if (!isAsyncEnvelope(env)) {
    // The wrapper returned an envelope that is neither sync-complete
    // nor a recognised async-202 shape. Treat as a hard error rather
    // than entering a poll loop with an unknown run_id.
    const err = new Error(
      'Fit analysis dispatch returned an unexpected envelope shape.',
    );
    err.code = 'UNEXPECTED_ENVELOPE';
    throw err;
  }
  if (!isAsyncPollingEnabled()) {
    const err = new Error(
      'Fit analysis is still running. Async polling is disabled in this build.',
    );
    err.code = 'ASYNC_POLLING_DISABLED';
    err.analyse_run_id = env.run_id;
    throw err;
  }

  // RC 1.49.7 W.2 — write the run marker so a tab refresh / tab
  // return mid-poll rebinds the in-flight UI without re-submitting.
  // The marker is best-effort: failure (Safari private mode, quota
  // exhausted) does NOT block dispatch — we just lose the
  // refresh-resilience bonus.
  if (markerScope) {
    writeRunMarker({
      ...markerScope,
      runId: env.run_id,
      startedAt: submittedAtMs,
      status: env.status || 'queued',
    });
  }

  if (signal?.aborted) {
    if (markerScope) clearRunMarker(markerScope);
    throw makeAbortError();
  }

  let polled;
  try {
    polled = await pollAnalyseRun(env.run_id, { signal, markerScope });
  } catch (pollErr) {
    if (markerScope) clearRunMarker(markerScope);
    throw pollErr;
  }

  // Terminal — clear the marker regardless of success/failure.
  if (markerScope) clearRunMarker(markerScope);

  if (polled.status === 'failed' || polled.status === 'cancelled') {
    const err = new Error(
      polled.error_detail || polled.error_code || 'Fit analysis did not complete.',
    );
    err.code = polled.error_code || 'ANALYSE_RUN_FAILED';
    err.analyse_run_id = polled.id;
    err.status = polled.status;
    throw err;
  }

  return synthesiseCanonical(polled);
}

// Internal helpers exposed for hermetic testing.
export const __testing = {
  pollAnalyseRun,
  synthesiseCanonical,
  isAsyncEnvelope,
  isSyncCompleteEnvelope,
  POLL_DELAYS_MS,
  MAX_POLLS,
};

// CWS-1 (2026-02-15, operator-locked) — public export for re-attach.
// Used by the workspace mount-reconcile path to RESUME polling on a
// known-good analyse_run_id after a tab/section switch unmounted the
// dispatching component. Read-only — never re-dispatches, never
// charges. Mirrors the role `pollInterviewPrepUntilTerminal` plays
// for Interview Prep recovery.
export { pollAnalyseRun, synthesiseCanonical };
